import { useUser } from '@plato/app-core/hooks/use-user';
import type { QuickFilter } from '@plato/ui-legacy/components/filters/lib/filter-utils';
import { mergeFilterOptions } from '@plato/ui-legacy/components/filters/lib/merge-filter-options';
import type { VerticalFilterOption } from '@plato/ui-legacy/components/filters/types/filter-types';
import { keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { getUsedFilterEventPayload } from '../../analytics/utils/filter-analytics.ts';
import { VerticalFilterTemplate } from '../../filters/templates/vertical-filter-template.tsx';
import type { TaskListFilters } from '../hooks/use-task-filters-state.ts';
import { useTaskListFilters } from '../hooks/use-task-filters-state.ts';

export function TaskOverviewFilters() {
    const user = useUser();
    const { t } = useTranslation();
    const sendEvent = useAnalytics();

    const { appliedFilters, setAppliedFilters, defaultFilters } = useTaskListFilters();

    const [filters, setFilters] = useState(appliedFilters);

    const { data, isLoading } = api.platoUser.query.tasks.getManyTasks.getFilterOptions.useQuery(filters, {
        placeholderData: keepPreviousData,
    });

    const { data: dataWithoutSearch } = api.platoUser.query.tasks.getManyTasks.getFilterOptions.useQuery(
        { ...filters, searchText: '' },
        { placeholderData: keepPreviousData },
    );

    const filtersConfig: VerticalFilterOption<TaskListFilters>[] = [
        {
            id: 'customerIds' as keyof TaskListFilters,
            label: t('filter_menu_title__customers', {
                ns: 'tasks',
            }),
            type: 'checkbox' as const,
            options: mergeFilterOptions(
                data?.customerIds,
                dataWithoutSearch?.customerIds,
                (o) => ({ id: o.value, label: o.label, value: o.value, count: o.count }),
                appliedFilters.customerIds,
            ),
        },
        {
            id: 'salesOrderIds' as keyof TaskListFilters,
            label: t('filter_menu_title__orders', { ns: 'tasks' }),
            type: 'checkbox' as const,
            options: mergeFilterOptions(
                data?.salesOrderIds,
                dataWithoutSearch?.salesOrderIds,
                (o) => ({ id: o.value, label: o.label, value: o.value, count: o.count }),
                appliedFilters.salesOrderIds,
            ),
        },
        {
            id: 'salesQuoteIds' as keyof TaskListFilters,
            label: t('filter_menu_title__quotes', { ns: 'tasks' }),
            type: 'checkbox' as const,
            options: mergeFilterOptions(
                data?.salesQuoteIds,
                dataWithoutSearch?.salesQuoteIds,
                (o) => ({ id: o.value, label: o.label, value: o.value, count: o.count }),
                appliedFilters.salesQuoteIds,
            ),
        },
        {
            id: 'assignedToIds' as keyof TaskListFilters,
            label: t('filter_menu_title__assigned_tos', { ns: 'tasks' }),
            type: 'checkbox' as const,
            options: mergeFilterOptions(
                data?.assignedToIds,
                dataWithoutSearch?.assignedToIds,
                (o) => ({ id: o.value, label: o.label, value: o.value, count: o.count }),
                appliedFilters.assignedToIds,
            ),
        },
        {
            id: 'assignedByIds' as keyof TaskListFilters,
            label: t('filter_menu_title__assigned_bys', { ns: 'tasks' }),
            type: 'checkbox' as const,
            options: mergeFilterOptions(
                data?.assignedByIds,
                dataWithoutSearch?.assignedByIds,
                (o) => ({ id: o.value, label: o.label, value: o.value, count: o.count }),
                appliedFilters.assignedByIds,
            ),
        },
    ];

    const quickFilters: QuickFilter<TaskListFilters>[] = useMemo(
        // eslint-disable-next-line react-hooks/preserve-manual-memoization -- legacy code
        () => [
            {
                id: 'to_me',
                label: t('tasks__quick_filters__to_me', { ns: 'tasks' }),
                filter: {
                    ...defaultFilters,
                    assignedToIds: [user.id],
                },
                showByDefault: true,
            },
            {
                id: 'by_me',
                label: t('tasks__quick_filters__by_me', { ns: 'tasks' }),
                filter: {
                    ...defaultFilters,
                    assignedByIds: [user.id],
                },
                showByDefault: true,
            },
        ],
        [user.id, t, defaultFilters],
    );

    const handleApplyFilters = (filters: TaskListFilters) => {
        setAppliedFilters(filters);

        sendEvent('task_overview_filter_used', getUsedFilterEventPayload(filters));
    };

    return (
        <VerticalFilterTemplate<TaskListFilters>
            aclSubject="Task"
            viewKey="tasks.overview"
            searchFilterKey="searchText"
            searchPlaceholder={t('tasks__search_placeholder', { ns: 'tasks' })}
            filters={filtersConfig}
            onFiltersChange={setFilters}
            itemCount={data?.totalCount || 0}
            isLoading={isLoading}
            defaultFilters={defaultFilters}
            appliedFilters={appliedFilters}
            onApplyFilters={handleApplyFilters}
            quickFilters={quickFilters}
        />
    );
}
